/**
 * The GM's private preview (`decisions/0203` point 7), as pure functions over the table's scene
 * (server state) and this client's preview (an encounter id, or null). *Peek* sets the preview: the
 * GM's stage shows that encounter and nobody else's changes. *Put on table* sets the table scene.
 * The preview is client-local and never in the URL; `Shell` holds it.
 */
import { encounterScene, NARRATIVE_SCENE, type TableScene } from "@lancer-vtt/domain";

/** What this client's stage shows: the preview when there is one, else the table's scene. */
export function stageScene(args: { table: TableScene; preview: string | null }): TableScene {
  return args.preview !== null ? encounterScene(args.preview) : args.table;
}

/** A preview ends once the table shows the previewed encounter; any other table change leaves it up. */
export function previewAfterTableChange(args: { preview: string | null; table: TableScene }): string | null {
  const { preview, table } = args;
  return table.kind === "encounter" && table.encounterId === preview ? null : preview;
}

/** One row of the ENCOUNTERS page's scene list. */
export type SceneRow = {
  scene: TableScene;
  /** The table sees this scene. */
  onTable: boolean;
  /** This client is previewing it. */
  previewing: boolean;
  /** *Peek* is offered: an encounter not on the table and not already previewed. */
  canPeek: boolean;
  /** *Put on table* is offered: any scene the table isn't already on. */
  canPut: boolean;
};

/** The Narrative row, then one row per encounter, in the given order. */
export function sceneRows(args: { table: TableScene; preview: string | null; encounters: ReadonlyArray<{ id: string }> }): SceneRow[] {
  const { table, preview, encounters } = args;
  const narrativeOnTable = table.kind === "narrative";
  const rows: SceneRow[] = [{ scene: NARRATIVE_SCENE, onTable: narrativeOnTable, previewing: false, canPeek: false, canPut: !narrativeOnTable }];
  for (const { id } of encounters) {
    const onTable = table.kind === "encounter" && table.encounterId === id;
    const previewing = preview === id;
    rows.push({ scene: encounterScene(id), onTable, previewing, canPeek: !onTable && !previewing, canPut: !onTable });
  }
  return rows;
}

/** The preview overlay's two names (`PEEKING <x> · ON TABLE: <y>`), or null when not previewing. */
export function previewBanner(args: {
  table: TableScene;
  preview: string | null;
  nameOf: (encounterId: string) => string | undefined;
}): { peeking: string; onTable: string } | null {
  const { table, preview, nameOf } = args;
  if (preview === null) return null;
  return { peeking: sceneLabel(encounterScene(preview), nameOf), onTable: sceneLabel(table, nameOf) };
}

/** A scene's upper-cased name: `NARRATIVE`, or the encounter's name (its id when it has none). */
export function sceneLabel(scene: TableScene, nameOf: (encounterId: string) => string | undefined): string {
  return scene.kind === "narrative" ? "NARRATIVE" : (nameOf(scene.encounterId) || scene.encounterId).toUpperCase();
}
